// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React /* eslint-disable-line no-unused-vars */, {
  type Element,
  useCallback,
} from 'react';
import {
  isNull,
  isString,
  isUndefined,
  take,
  takeRight,
  toArray,
} from 'lodash';

import { Colors } from '@zapier/theme';
import { Colors as DesignSystemColors } from '@zapier/design-system';
import { createKeyHandler } from '@zapier/common-utils';
import { jsx } from '@emotion/core';
import { Icon, LogoImage } from '@zapier/common-components';

/** @jsx jsx */

type PillValue = string | Element<*>;

type Props = {
  api: ?string,
  isAutoMapped: boolean,
  onClick: () => void,
  stepIndex: ?number,
  name: string,
  value: PillValue,
};

const fontSize = 14;
const iconSize = 12;
const logoSize = 14;

const previewPillContainerCss = (isClickable: boolean) => ({
  backgroundColor: DesignSystemColors.grey100,
  borderRadius: 5,
  cursor: isClickable ? 'pointer' : 'default',
  display: 'inline',
  padding: `1px 4px`,
  ...(isClickable
    ? {
        '&:hover': {
          backgroundColor: DesignSystemColors.grey200,
        },
        '&:focus': {
          boxShadow: `0 0 0px 2px ${Colors.blue500}`,
          outline: 'none',
          padding: `0px 4px`,
        },
      }
    : {}),
});

const previewPillCss = {
  backgroundColor: DesignSystemColors.inherit,
  display: 'inline',
  fontSize,
};

const pillLogoContainerCss = {
  // Crazy position hack. I probably just don't know the right
  // box-model incantation, but I got tired... so $CssFixMe
  position: 'relative',
  top: -1,
};

const pillNameCss = {
  backgroundColor: DesignSystemColors.inherit,
  color: DesignSystemColors.black,
  display: 'inline',
  fontWeight: 600,
};

const pillValueCss = {
  backgroundColor: DesignSystemColors.inherit,
  color: DesignSystemColors.grey700,
  display: 'inline',
};

const maybeTruncatePillValue = (value: PillValue) => {
  if (isString(value) && value.length > 30) {
    const start = take(toArray(value), 15).join('');
    const end = takeRight(toArray(value), 15).join('');
    return `${start}…${end}`;
  }
  return value;
};

const PreviewPill = ({
  api,
  isAutoMapped,
  onClick,
  stepIndex,
  name,
  value,
}: Props) => {
  const onKeyDown = useCallback(createKeyHandler(['enter', 'space'], onClick));
  const divProps = onClick
    ? {
        onKeyDown,
        onClick,
        tabIndex: 0,
        role: 'combobox',
      }
    : {};

  const hasStepIndex = !isUndefined(stepIndex) && !isNull(stepIndex);
  const label = hasStepIndex ? `${stepIndex + 1}. ${name}` : name;

  return (
    <span css={previewPillContainerCss(Boolean(onClick))} {...divProps}>
      <span css={previewPillCss}>
        <span css={pillLogoContainerCss}>
          <LogoImage
            app={api}
            backgroundType="clear"
            borderPadding={0}
            size={logoSize}
          />
        </span>{' '}
        <span css={pillNameCss}>{label}:</span>{' '}
        <span css={pillValueCss} title={value}>
          {maybeTruncatePillValue(value)}
        </span>
        {isAutoMapped && (
          <span>
            {' '}
            <Icon color={Colors.steel} name="wand" size={iconSize} />
          </span>
        )}
      </span>
    </span>
  );
};

export default PreviewPill;
